import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import HotelIcon from '@mui/icons-material/Hotel';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BoltIcon from '@mui/icons-material/Bolt';
import BlockIcon from '@mui/icons-material/Block';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import BuildIcon from '@mui/icons-material/Build';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import GroupsIcon from '@mui/icons-material/Groups';
import MaintenanceRoomIndicator from '../housekeeping/MaintenanceRoomIndicator';
import { getMaintenanceCardAccentSx, resolveRoomMaintenanceSummary } from '../../utils/maintenanceRoomUtils';
import { resolveRoomBookingChannelLogoSrc } from '../../utils/staticUtils';
import { formatHotelDate } from '../../utils/dateUtils';
import { DASHBOARD_ACCENT } from '../../utils/dashboardSurfaceSx';
import moment from 'moment';
import {
  getCleanStatusMeta,
  getUltraRoomAvatarSize,
  getUltraRoomAvatarSx,
  getUltraRoomAvatarImageSx,
  getUltraRoomBodyChipSx,
  getUltraRoomBookingCodeSx,
  getUltraRoomCardBodySx,
  getUltraRoomCardFooterSx,
  getUltraRoomCardSx,
  getUltraRoomCardTextSx,
  getUltraRoomCardMinHeight,
  getUltraRoomChannelBadgeSx,
  getUltraRoomGuestNameSx,
  getUltraRoomHeroSx,
  getUltraRoomMenuButtonSx,
  getUltraRoomMetaSx,
  getUltraRoomNumberSx,
  getUltraRoomStatusChipSx,
} from '../../utils/dashboardUltraGridLayout';

const BLOCK_INDICATOR_COLOR = '#ef5350';
const WEB_CHECKIN_ACCENT = DASHBOARD_ACCENT;

function formatWebCheckInCountdown(expiresAt, nowMs = Date.now()) {
  if (!expiresAt) return '';
  const end = moment(expiresAt);
  if (!end.isValid()) return '';
  const remainingMs = end.valueOf() - nowMs;
  if (remainingMs <= 0) return 'Expired';
  const totalSec = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${String(mins).padStart(2, '0')}m`;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

function BlockRoomIndicator({ blockInfo, isDark = false }) {
  if (!blockInfo) return null;

  const reason = String(blockInfo.reason || '').trim() || 'No reason provided';
  const fromLabel = blockInfo.original_start_date
    ? formatHotelDate(blockInfo.original_start_date)
    : '—';
  const toLabel = blockInfo.original_end_date
    ? formatHotelDate(blockInfo.original_end_date)
    : '—';
  const blockType = blockInfo.block_type_display || blockInfo.block_type || 'Block';

  return (
    <Tooltip
      title={
        <Box sx={{ maxWidth: 260 }}>
          <Typography variant="caption" sx={{ fontWeight: 800, display: 'block', mb: 0.5 }}>
            {blockType}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4, mb: 0.5 }}>
            {reason}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.9 }}>
            From {fromLabel} to {toLabel}
          </Typography>
        </Box>
      }
      arrow
    >
      <IconButton
        size="small"
        aria-label="Inventory block details"
        onClick={(event) => event.stopPropagation()}
        sx={{
          width: 28,
          height: 28,
          p: 0,
          flexShrink: 0,
          color: BLOCK_INDICATOR_COLOR,
          bgcolor: isDark ? alpha(BLOCK_INDICATOR_COLOR, 0.16) : alpha(BLOCK_INDICATOR_COLOR, 0.1),
          border: `1px solid ${alpha(BLOCK_INDICATOR_COLOR, isDark ? 0.45 : 0.35)}`,
          boxShadow: `0 0 10px ${alpha(BLOCK_INDICATOR_COLOR, 0.28)}`,
          '&:hover': {
            bgcolor: isDark ? alpha(BLOCK_INDICATOR_COLOR, 0.26) : alpha(BLOCK_INDICATOR_COLOR, 0.18),
          },
        }}
      >
        <BlockIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}

function ultraRoomSkeletonSx(isDark) {
  return {
    bgcolor: isDark ? alpha('#ffffff', 0.07) : alpha('#94a3b8', 0.14),
  };
}

function UltraRoomGridCardSkeleton({
  isDark,
  isMobileLayout,
  isWorkspaceLayout = false,
  avatarSize,
  colors,
}) {
  const loadingColors = {
    dot: isDark ? 'rgba(148, 163, 184, 0.55)' : 'rgba(100, 116, 139, 0.45)',
    border: isDark ? alpha('#94a3b8', 0.28) : alpha('#94a3b8', 0.2),
    shadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.35)' : '0 1px 4px rgba(15, 23, 42, 0.06)',
  };
  const bone = ultraRoomSkeletonSx(isDark);

  return (
    <Box sx={{ minWidth: 0, height: 'auto', minHeight: getUltraRoomCardMinHeight(isMobileLayout, isWorkspaceLayout), display: 'flex', flexDirection: 'column' }}>
      <Paper
        elevation={0}
        sx={getUltraRoomCardSx(isDark, isMobileLayout, loadingColors, {
          showAsDisabled: false,
          clickable: false,
          isWorkspace: isWorkspaceLayout,
        })}
      >
      <Box sx={getUltraRoomHeroSx(isMobileLayout, isDark, loadingColors)}>
        <Skeleton variant="circular" width={avatarSize} height={avatarSize} animation="wave" sx={bone} />
        <Skeleton variant="text" width={56} height={30} animation="wave" sx={{ mt: 0.75, ...bone }} />
      </Box>
      <Box sx={getUltraRoomCardBodySx(isDark, isMobileLayout, colors)}>
        <Box sx={getUltraRoomCardTextSx(isMobileLayout)}>
          <Skeleton variant="text" width="72%" height={14} animation="wave" sx={{ mx: 'auto', ...bone }} />
          <Skeleton variant="text" width="58%" height={11} animation="wave" sx={{ mx: 'auto', ...bone }} />
          <Skeleton variant="text" width="64%" height={10} animation="wave" sx={{ mx: 'auto', ...bone }} />
        </Box>
        <Box sx={getUltraRoomCardFooterSx(isMobileLayout)}>
          <Skeleton variant="rounded" width={48} height={22} animation="wave" sx={{ borderRadius: 999, ...bone }} />
          <Skeleton variant="rounded" width={56} height={22} animation="wave" sx={{ borderRadius: 999, ...bone }} />
        </Box>
        </Box>
      </Paper>
    </Box>
  );
}

function UltraRoomGridCard({
  room,
  status,
  statusLabel,
  colors,
  isDark,
  isMobileLayout = false,
  isWorkspaceLayout = false,
  isLoading = false,
  nameLine,
  nameLineMuted = false,
  booking,
  bookingCode = '',
  showAsDisabled = false,
  quickEligible = false,
  webCheckInEligible = false,
  checkoutEligible = false,
  checkInEligible = false,
  blockEligible = false,
  cleaningEligible = false,
  dirtyRoomEligible = false,
  maintenanceBlocked = false,
  maintenanceBlockLabel = '',
  blockInfo = null,
  activeWebCheckIn = null,
  onCardClick,
  onQuickBook,
  onWebCheckIn,
  onViewBooking,
  onCheckIn,
  onCheckout,
  onEventBlock,
  onMarkCleaning,
  onOpenWebCheckInInvite,
}) {
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const menuOpen = Boolean(menuAnchor);
  const roomImg = room?.room_type_image_url;
  const avatarSize = getUltraRoomAvatarSize(isMobileLayout);
  const showSkeleton = isLoading;
  const cardMinHeight = getUltraRoomCardMinHeight(isMobileLayout, isWorkspaceLayout);
  const hasActiveWebCheckIn = Boolean(activeWebCheckIn?.id || room?.has_active_web_checkin);
  const webCheckInInvite = activeWebCheckIn || room?.active_web_checkin || null;

  // Local countdown only — do not tick the whole Ultra board every second.
  useEffect(() => {
    if (!hasActiveWebCheckIn || !webCheckInInvite?.expires_at) return undefined;
    setNowMs(Date.now());
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [hasActiveWebCheckIn, webCheckInInvite?.expires_at]);

  const webCheckInCountdown = useMemo(
    () => (hasActiveWebCheckIn ? formatWebCheckInCountdown(webCheckInInvite?.expires_at, nowMs) : ''),
    [hasActiveWebCheckIn, webCheckInInvite?.expires_at, nowMs]
  );

  const roomNum = room?.room_number || `Room ${room?.id}`;
  const roomCode = String(room?.room_code || '').trim();
  const roomTypeName = room?.room_type_name || '';
  const cleanMeta = getCleanStatusMeta(room?.clean_status);
  const blockedManageEligible = status === 'blocked' && Boolean(onEventBlock);
  const showBlockIndicator = status === 'blocked' && Boolean(blockInfo);
  const maintenanceSummary = resolveRoomMaintenanceSummary(room);
  const hasMaintenance = Boolean(maintenanceSummary.hasMaintenance);
  const showActionsMenu =
    !showSkeleton
    && (
      quickEligible
      || webCheckInEligible
      || Boolean(booking && onViewBooking)
      || checkInEligible
      || checkoutEligible
      || blockEligible
      || blockedManageEligible
      || cleaningEligible
      || hasMaintenance
      || (hasActiveWebCheckIn && Boolean(onOpenWebCheckInInvite))
    );
  const clickable = !showSkeleton && (
    hasActiveWebCheckIn
    || quickEligible
    || dirtyRoomEligible
    || Boolean(booking && onViewBooking)
    || blockedManageEligible
  );
  const metaLine = [roomTypeName, roomCode].filter(Boolean).join(' · ');
  const displayNameLine = hasActiveWebCheckIn
    ? (webCheckInInvite?.guest_name || 'Web check-in pending')
    : nameLine;
  const displayNameMuted = hasActiveWebCheckIn ? false : nameLineMuted;
  const statusChipLabel = hasActiveWebCheckIn
    ? 'Web Check-in'
    : (maintenanceBlocked ? 'Maintenance' : statusLabel);
  const statusChipColor = hasActiveWebCheckIn
    ? WEB_CHECKIN_ACCENT
    : (maintenanceBlocked ? '#ef5350' : colors.dot);
  const channelLogoSrc = resolveRoomBookingChannelLogoSrc(booking);
  const isGroupMember = Boolean(
    booking?.group_booking_id
    || booking?.group_booking_code
    || booking?.is_group_member
  );
  const groupBadgeTitle = booking?.group_booking_code
    ? `Group ${booking.group_booking_code}`
    : 'Group booking';
  const isWebCheckInBooking = Boolean(
    booking?.from_web_checkin
    || booking?.meta?.from_web_checkin
    || booking?.meta?.source === 'web_checkin'
  );
  const showHeroActionColumn =
    hasMaintenance
    || showActionsMenu
    || showBlockIndicator
    || hasActiveWebCheckIn
    || isWebCheckInBooking;

  const handleMenuOpen = (event) => {
    if (showSkeleton) return;
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => setMenuAnchor(null);

  if (showSkeleton) {
    return (
      <UltraRoomGridCardSkeleton
        isDark={isDark}
        isMobileLayout={isMobileLayout}
        isWorkspaceLayout={isWorkspaceLayout}
        avatarSize={avatarSize}
        colors={colors}
      />
    );
  }

  return (
    <>
      <Box sx={{ minWidth: 0, height: 'auto', minHeight: cardMinHeight, display: 'flex', flexDirection: 'column' }}>
        <Paper
          elevation={0}
          title={
            hasActiveWebCheckIn
              ? `Web check-in pending${webCheckInCountdown ? ` · expires in ${webCheckInCountdown}` : ''}`
              : maintenanceBlocked
              ? maintenanceBlockLabel || 'Blocked for maintenance'
              : dirtyRoomEligible
                ? 'Mark room clean before booking'
                : status === 'hourly' && checkoutEligible
                  ? 'Hourly stay — click to check out this guest'
                  : status === 'pending_checkout' && checkoutEligible
                  ? 'Checkout pending — click to check out this guest'
                  : quickEligible
                    ? status === 'cancelled'
                      ? 'Quick book — room is free after cancellation'
                      : 'Quick book this room for the selected date'
                    : blockedManageEligible
                      ? 'View inventory blocks for this room type'
                      : undefined
          }
          onClick={onCardClick}
          sx={[
            getUltraRoomCardSx(isDark, isMobileLayout, colors, {
              showAsDisabled: showAsDisabled && !hasActiveWebCheckIn,
              clickable,
              isWorkspace: isWorkspaceLayout,
            }),
            hasActiveWebCheckIn
              ? {
                  border: `1.5px solid ${alpha(WEB_CHECKIN_ACCENT, isDark ? 0.55 : 0.42)}`,
                  boxShadow: isDark
                    ? `0 0 0 1px ${alpha(WEB_CHECKIN_ACCENT, 0.22)}, 0 10px 24px ${alpha('#000', 0.35)}`
                    : `0 0 0 1px ${alpha(WEB_CHECKIN_ACCENT, 0.18)}, 0 8px 18px ${alpha(WEB_CHECKIN_ACCENT, 0.12)}`,
                  '&::before': {
                    background: `linear-gradient(90deg, ${alpha(WEB_CHECKIN_ACCENT, 0.2)} 0%, ${WEB_CHECKIN_ACCENT} 50%, ${alpha(WEB_CHECKIN_ACCENT, 0.2)} 100%)`,
                  },
                }
              : null,
            hasMaintenance ? getMaintenanceCardAccentSx(maintenanceSummary.priority, isDark) : null,
          ]}
        >
        <Box sx={getUltraRoomHeroSx(isMobileLayout, isDark, colors)}>
          {!isMobileLayout ? (
            <Chip
              size="small"
              label={statusChipLabel}
              sx={getUltraRoomStatusChipSx(statusChipColor, isDark)}
            />
          ) : null}

          {showHeroActionColumn ? (
            <Box
              sx={{
                position: 'absolute',
                top: 6,
                right: 6,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.35,
              }}
            >
              {showActionsMenu ? (
                <IconButton
                  size="small"
                  aria-label="Room actions"
                  onClick={handleMenuOpen}
                  sx={getUltraRoomMenuButtonSx(isDark)}
                >
                  <MoreVertIcon sx={{ fontSize: 16 }} />
                </IconButton>
              ) : null}
              {showBlockIndicator ? (
                <BlockRoomIndicator blockInfo={blockInfo} isDark={isDark} />
              ) : null}
              {hasMaintenance ? (
                <MaintenanceRoomIndicator room={room} isDark={isDark} variant="icon" />
              ) : null}
              {isWebCheckInBooking || hasActiveWebCheckIn ? (
                <Tooltip
                  title={
                    hasActiveWebCheckIn
                      ? (webCheckInCountdown ? `Expires in ${webCheckInCountdown}` : 'Web check-in pending')
                      : 'Booked via web check-in'
                  }
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: alpha(WEB_CHECKIN_ACCENT, isDark ? 0.22 : 0.9),
                      color: isDark ? WEB_CHECKIN_ACCENT : '#031512',
                      border: `1px solid ${alpha(WEB_CHECKIN_ACCENT, 0.45)}`,
                    }}
                  >
                    <HowToRegIcon sx={{ fontSize: 14 }} />
                  </Box>
                </Tooltip>
              ) : null}
            </Box>
          ) : null}

          <Box sx={{ position: 'relative', zIndex: 1, mt: isMobileLayout ? 0.35 : 0.45 }}>
            <Avatar
              src={roomImg || undefined}
              alt={roomTypeName || roomNum}
              imgProps={{
                loading: isWorkspaceLayout ? 'eager' : 'lazy',
                decoding: 'async',
                style: getUltraRoomAvatarImageSx(),
              }}
              sx={getUltraRoomAvatarSx(isMobileLayout, isDark, colors)}
            >
              <HotelIcon sx={{ fontSize: isMobileLayout ? 22 : 26 }} />
            </Avatar>

            {isGroupMember ? (
              <Box
                title={groupBadgeTitle}
                sx={{
                  position: 'absolute',
                  left: -1,
                  bottom: -1,
                  zIndex: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: isMobileLayout ? 18 : 20,
                  height: isMobileLayout ? 18 : 20,
                  borderRadius: '50%',
                  bgcolor: isDark ? '#0f172a' : '#ffffff',
                  border: `1.5px solid ${alpha(DASHBOARD_ACCENT, 0.85)}`,
                  boxShadow: isDark
                    ? '0 1px 4px rgba(0,0,0,0.45)'
                    : '0 1px 4px rgba(15,23,42,0.18)',
                  pointerEvents: 'none',
                }}
              >
                <GroupsIcon sx={{ fontSize: isMobileLayout ? 11 : 12, color: DASHBOARD_ACCENT }} />
              </Box>
            ) : null}

            {channelLogoSrc ? (
              <Box sx={getUltraRoomChannelBadgeSx(isMobileLayout)}>
                <Box
                  component="img"
                  src={channelLogoSrc}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
            ) : null}
          </Box>

          <Typography component="div" sx={getUltraRoomNumberSx(isMobileLayout, isDark, colors)}>
            {roomNum}
          </Typography>
        </Box>

        <Box sx={getUltraRoomCardBodySx(isDark, isMobileLayout, colors)}>
          <Box sx={getUltraRoomCardTextSx(isMobileLayout)}>
            <Typography sx={getUltraRoomGuestNameSx(isDark, colors, displayNameMuted, isMobileLayout)}>
              {displayNameLine}
            </Typography>
            {metaLine ? (
              <Typography sx={getUltraRoomMetaSx(isDark)}>{metaLine}</Typography>
            ) : null}
            {bookingCode ? (
              <Typography sx={getUltraRoomBookingCodeSx(isDark)}>#{bookingCode}</Typography>
            ) : (
              <Typography
                aria-hidden
                sx={{
                  ...getUltraRoomBookingCodeSx(isDark),
                  visibility: 'hidden',
                  userSelect: 'none',
                }}
              >
                #
              </Typography>
            )}
          </Box>

          <Box sx={getUltraRoomCardFooterSx(isMobileLayout)}>
            <Chip size="small" label={cleanMeta.label} sx={getUltraRoomBodyChipSx(cleanMeta.color, isDark)} />
            {hasActiveWebCheckIn ? (
              <Chip
                size="small"
                label={webCheckInCountdown || 'Pending'}
                sx={getUltraRoomBodyChipSx(WEB_CHECKIN_ACCENT, isDark)}
              />
            ) : status !== 'vacant' ? (
              <Chip size="small" label={statusLabel} sx={getUltraRoomBodyChipSx(colors.dot, isDark)} />
            ) : null}
          </Box>
        </Box>
      </Paper>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 168,
              borderRadius: '12px',
              bgcolor: isDark ? 'rgba(3,7,18,0.96)' : '#fff',
              border: `1px solid ${isDark ? alpha('#ffffff', 0.12) : alpha('#000', 0.1)}`,
              color: isDark ? '#f8fafc' : '#0f172a',
              '& .MuiMenuItem-root': {
                color: isDark ? '#f8fafc' : '#0f172a',
              },
            },
          },
        }}
      >
        {quickEligible ? (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              // Prefer dedicated handler so pending-checkout card click (checkout)
              // is not reused when staff pick Quick Book from the menu.
              if (onQuickBook) {
                onQuickBook();
                return;
              }
              onCardClick?.();
            }}
          >
            <ListItemIcon>
              <BoltIcon fontSize="small" sx={{ color: '#4ce1b6' }} />
            </ListItemIcon>
            <ListItemText
              primary="Quick Book"
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
            />
          </MenuItem>
        ) : null}
        {hasActiveWebCheckIn && onOpenWebCheckInInvite ? (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onOpenWebCheckInInvite(webCheckInInvite);
            }}
          >
            <ListItemIcon>
              <HowToRegIcon fontSize="small" sx={{ color: WEB_CHECKIN_ACCENT }} />
            </ListItemIcon>
            <ListItemText
              primary="View Web Check-in"
              secondary={webCheckInCountdown ? `Expires ${webCheckInCountdown}` : undefined}
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
              secondaryTypographyProps={{ fontSize: '0.72rem' }}
            />
          </MenuItem>
        ) : null}
        {webCheckInEligible && onWebCheckIn ? (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onWebCheckIn();
            }}
          >
            <ListItemIcon>
              <LoginIcon fontSize="small" sx={{ color: '#4ce1b6' }} />
            </ListItemIcon>
            <ListItemText
              primary="Web Check-in"
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
            />
          </MenuItem>
        ) : null}
        {booking && onViewBooking ? (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onViewBooking();
            }}
          >
            <ListItemIcon>
              <VisibilityIcon fontSize="small" sx={{ color: '#60a5fa' }} />
            </ListItemIcon>
            <ListItemText
              primary="View Booking"
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
            />
          </MenuItem>
        ) : null}
        {checkInEligible && onCheckIn ? (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onCheckIn();
            }}
          >
            <ListItemIcon>
              <LoginIcon fontSize="small" sx={{ color: '#60a5fa' }} />
            </ListItemIcon>
            <ListItemText
              primary="Check-in"
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
            />
          </MenuItem>
        ) : null}
        {checkoutEligible && onCheckout ? (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onCheckout();
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" sx={{ color: '#4ce1b6' }} />
            </ListItemIcon>
            <ListItemText
              primary="Checkout"
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
            />
          </MenuItem>
        ) : null}
        {(blockEligible || blockedManageEligible) && onEventBlock ? (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onEventBlock();
            }}
          >
            <ListItemIcon>
              <BlockIcon fontSize="small" sx={{ color: '#ffb74d' }} />
            </ListItemIcon>
            <ListItemText
              primary="Event Block"
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
            />
          </MenuItem>
        ) : null}
        {cleaningEligible && onMarkCleaning ? (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              onMarkCleaning();
            }}
          >
            <ListItemIcon>
              <CleaningServicesIcon fontSize="small" sx={{ color: '#fb7185' }} />
            </ListItemIcon>
            <ListItemText
              primary="Mark Clean"
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
            />
          </MenuItem>
        ) : null}
        {hasMaintenance ? (
          <MenuItem
            onClick={() => {
              handleMenuClose();
              navigate('/housekeeping/maintenance/');
            }}
          >
            <ListItemIcon>
              <BuildIcon fontSize="small" sx={{ color: '#ef5350' }} />
            </ListItemIcon>
            <ListItemText
              primary="Maintenance"
              primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600 }}
            />
          </MenuItem>
        ) : null}
      </Menu>
    </>
  );
}

export default React.memo(UltraRoomGridCard);
